import type WhiteboardController from '../../controllers/WhiteboardController';
import type { HierarchyImportRelationshipType } from '../../types';
import type { DiagramNode, RenderToWhiteboardOptions } from '@aha-app/diagrams';
import { RECORD_DEFAULT_HEIGHT, RECORD_DEFAULT_WIDTH } from '../../constants';
import Serialization from '../../collaboration/Serialization';
import {
  type HierarchyImportProps,
  type PositionOffset,
  HierarchyImportError,
  ImportConfig,
} from './HierarchyImportTypes';
import {
  DiagramLayoutService,
  type DiagramLayout,
} from './DiagramLayoutService';
import { HierarchyData } from './HierarchyData';
import { hasRecordReference, isShapeRecord } from '../../objects/guards';
import { type ChartOrientation } from './HierarchyResultTransformer';

function getOrientation(
  relationshipType: HierarchyImportRelationshipType
): ChartOrientation {
  switch (relationshipType) {
    case 'parents':
      return 'north';
    case 'children':
      return 'south';
    case 'dependencies':
    case 'related':
    default:
      return 'east';
  }
}

function getRelationshipInfo(
  relationshipType: HierarchyImportRelationshipType,
  count: number
) {
  let base: string;
  switch (relationshipType) {
    case 'parents':
      base = 'parent';
      break;
    case 'children':
      base = 'child';
      break;
    case 'dependencies':
      base = 'dependent';
      break;
    case 'related':
    default:
      base = 'related';
  }

  return {
    type: base,
    text: `${base}${count === 1 ? ' record' : ' records'}`,
  };
}

function getEdgeAttrs(
  controller: WhiteboardController,
  linkType: string,
  relationshipType: HierarchyImportRelationshipType
) {
  const connectorProps =
    controller.dependencyLineManager?.getConnectorProperties(linkType);

  // Need to reverse endStyles for parents, because we want diagram to flow towards selected record
  if (relationshipType === 'parents' && connectorProps?.endStyles) {
    connectorProps.endStyles.reverse();
  }

  return connectorProps;
}

async function fetchHierarchyData(
  props: HierarchyImportProps
): Promise<HierarchyData> {
  const { selection, controller, relationshipType } = props;
  const recordId = selection.recordId;
  const recordType = selection.recordType;

  if (
    !selection ||
    !isShapeRecord(selection) ||
    !hasRecordReference(selection)
  ) {
    console.error('Invalid selection for record import');
    throw new HierarchyImportError(
      'VALIDATION_ERROR',
      'Selection must be a valid shape record with record reference'
    );
  }

  if (
    !controller.relatedRecordManager?.supportedRecordTypes.includes(recordType)
  ) {
    console.error('Invalid selection for record import');
    throw new HierarchyImportError(
      'VALIDATION_ERROR',
      `Record type ${recordType} is not supported`
    );
  }

  try {
    const hierarchyResult =
      await controller.relatedRecordManager.fetchHierarchy(
        recordId,
        recordType,
        relationshipType,
        {
          maxRecords: ImportConfig.MAX_RECORDS,
          alreadyFetched: new Set<string>(),
        }
      );

    return new HierarchyData(hierarchyResult);
  } catch (error) {
    throw HierarchyImportError.network(error as Error);
  }
}

async function generateDiagramLayout(
  hierarchyData: HierarchyData,
  relationshipType: HierarchyImportRelationshipType
): Promise<DiagramLayout> {
  const layoutService = new DiagramLayoutService();
  return await layoutService.generateLayout(hierarchyData, relationshipType);
}

function findSelectedNode(
  layout: DiagramLayout,
  recordId: string
): DiagramNode {
  const selectedNode = layout.findNodeByTitle(recordId);
  if (!selectedNode) {
    throw HierarchyImportError.layout(
      `Selected record ${recordId} not found in hierarchy diagram`
    );
  }
  return selectedNode;
}

function calculatePositionOffset(
  props: HierarchyImportProps,
  selectedDiagramNode: DiagramNode
): PositionOffset {
  let offsetX = props.selection.left - selectedDiagramNode.layout.left;
  let offsetY = props.selection.top - selectedDiagramNode.layout.top;

  const orientation = getOrientation(props.relationshipType);
  switch (orientation) {
    case 'east':
      offsetX += RECORD_DEFAULT_WIDTH;
      break;
    case 'south':
      offsetY += RECORD_DEFAULT_HEIGHT;
      break;
    case 'west':
      offsetX -= RECORD_DEFAULT_WIDTH;
      break;
    case 'north':
      offsetY -= RECORD_DEFAULT_HEIGHT;
      break;
  }

  return { x: offsetX, y: offsetY };
}

function createNodeAttrsFunction(
  hierarchyData: HierarchyData,
  positionOffset: PositionOffset
) {
  return (node: DiagramNode) => {
    const hierarchyNode = hierarchyData.getNodeByRecordId(
      node.title || node.id
    );
    if (!hierarchyNode) return {};

    return {
      recordId: hierarchyNode.record.recordId,
      recordType: hierarchyNode.record.recordType,
      shapeName: 'record',
      width: RECORD_DEFAULT_WIDTH,
      height: RECORD_DEFAULT_HEIGHT,
      left: node.layout.left + positionOffset.x,
      top: node.layout.top + positionOffset.y,
    };
  };
}

function createEdgeAttrsFunction(
  props: HierarchyImportProps,
  hierarchyData: HierarchyData
) {
  return (parentNode: DiagramNode, childNode: DiagramNode) => {
    const parentHierarchyNode = hierarchyData.getNodeByRecordId(
      parentNode.title || parentNode.id
    );
    const childHierarchyNode = hierarchyData.getNodeByRecordId(
      childNode.title || childNode.id
    );

    if (!parentHierarchyNode || !childHierarchyNode) {
      return {};
    }

    const isChildToParentRelation = parentHierarchyNode.parentIds.includes(
      childHierarchyNode.record.recordId
    );
    const isParentToChildRelation = parentHierarchyNode.childIds.includes(
      childHierarchyNode.record.recordId
    );

    let linkType: string | undefined;
    let relationDescription: string | undefined;

    if (isParentToChildRelation) {
      linkType = childHierarchyNode.linkType;
      relationDescription = childHierarchyNode.relationDescription;
    } else if (isChildToParentRelation) {
      linkType = parentHierarchyNode.linkType;
      relationDescription = parentHierarchyNode.relationDescription;
    }

    if (!linkType) {
      return {};
    }

    const connectorProperties = getEdgeAttrs(
      props.controller,
      linkType,
      props.relationshipType
    );

    return {
      ...(connectorProperties || {}),
      linkType,
      label: relationDescription || 'Contains',
      labelPosition: isParentToChildRelation ? 0.8 : 0.3,
    };
  };
}

function createWhiteboardObjects(
  props: HierarchyImportProps,
  hierarchyData: HierarchyData,
  layout: DiagramLayout,
  selectedDiagramNode: DiagramNode
): any {
  const positionOffset = calculatePositionOffset(props, selectedDiagramNode);

  const renderOptions: RenderToWhiteboardOptions = {
    nodeAttrs: createNodeAttrsFunction(hierarchyData, positionOffset),
    edgeAttrs: createEdgeAttrsFunction(props, hierarchyData),
  };

  const json = layout.diagram.renderToWhiteboard(renderOptions);

  // Update connector attachments and remove duplicate selected record
  Object.values(json.objects).forEach((obj: any) => {
    if (obj.type === 'connector' && obj.attachments) {
      obj.attachments.forEach((attachment: any) => {
        if (attachment.objectId === selectedDiagramNode.id) {
          attachment.objectId = props.selection.id;
        }
      });
    }
  });

  delete json.objects[selectedDiagramNode.id];

  return json;
}

function getRecordCount(json: any): number {
  return Object.keys(json.objects).filter(
    id =>
      json.objects[id].type === 'shape' &&
      json.objects[id].shapeName === 'record'
  ).length;
}

function showSuccessMessage(
  props: HierarchyImportProps,
  whiteboardJson: any
): void {
  const recordCount = getRecordCount(whiteboardJson);
  const relationshipInfo = getRelationshipInfo(
    props.relationshipType,
    recordCount
  );
  props.controller.actionShowMessage({
    message: `Imported ${recordCount} ${relationshipInfo.text}`,
    type: 'success',
  });
}

function handleImportError(
  controller: WhiteboardController,
  error: Error
): void {
  const structuredError =
    error instanceof HierarchyImportError
      ? error
      : HierarchyImportError.layout(error.message);

  console.error('HierarchyImport error:', structuredError);

  const userMessage = getUserFriendlyMessage(structuredError);
  controller.actionShowMessage({
    message: userMessage,
    type: 'error',
  });
}

function getUserFriendlyMessage(error: HierarchyImportError): string {
  switch (error.code) {
    case 'NETWORK_ERROR':
      return 'Error while fetching records. Please contact support.';
    case 'VALIDATION_ERROR':
      return 'Invalid selection. Please select a supported record type.';
    case 'LAYOUT_ERROR':
      return 'Unable to generate hierarchy layout. Please contact support.';
    default:
      return 'Unable to import record hierarchy. Please contact support.';
  }
}

export async function importHierarchy(
  props: HierarchyImportProps
): Promise<void> {
  try {
    const hierarchyData = await fetchHierarchyData(props);
    const layout = await generateDiagramLayout(
      hierarchyData,
      props.relationshipType
    );
    const selectedNode = findSelectedNode(layout, props.selection.recordId);
    const whiteboardJson = createWhiteboardObjects(
      props,
      hierarchyData,
      layout,
      selectedNode
    );

    Serialization.restoreDocument(props.controller, whiteboardJson);
    showSuccessMessage(props, whiteboardJson);
  } catch (error) {
    handleImportError(props.controller, error as Error);
  }
}
